<script lang="ts">
  import { run, preventDefault } from "svelte/legacy";

  import { useQueryClient } from "@sveltestack/svelte-query";
  import { useCheckPurchase, useGetProducts } from "../../helpers/api";
  import { getAvaliableProducts } from "../../helpers/helperMethods";
  import { getProductWithId } from "../../helpers/priceCalculator";
  import {
    CheckPurchaseEnum,
    type PurchaseFlowType,
    type AvaliableProducts,
    type FacilityInformation,
    type Person,
    type Product,
    type TResponse,
  } from "../../helpers/types";
  import { onMount } from "svelte";
  import ZipCodeError from "../molecules/ZipCodeError.svelte";
  import Error from "../molecules/Error.svelte";
  import Loader from "../atoms/Loader.svelte";
  import ProductSelector from "./ProductSelector.svelte";
  import Button from "../atoms/Button.svelte";
  import Card from "../atoms/Card.svelte";
  import ErrorMessage from "../ErrorMessage.svelte";
  import Heading from "../atoms/Heading.svelte";
  import Text from "../atoms/Text.svelte";
  import ErrorBanner from "../atoms/ErrorBanner.svelte";

  interface Props {
    estimation: number;
    selectedProductId: number | null;
    selectedProduct: Product | undefined;
    zipcode: string | undefined;
    campaign: string;
    productOrder: string[];
    removeSelectedProductId: () => void;
    goBack: () => void;
    onClickContinue: (e: Event) => void;
    purchasekey?: string;
    setUserData?: (
      facilityInformation: FacilityInformation,
      person: Person,
      requireLimeFormPurchase: boolean
    ) => void | undefined;
    children?: import("svelte").Snippet;
    purchaseFlowType: PurchaseFlowType;
    showSelectProductError?: boolean;
    avaliableProducts?: AvaliableProducts;
  }

  let {
    estimation,
    selectedProductId = $bindable(),
    selectedProduct = $bindable(),
    zipcode = $bindable(),
    campaign,
    productOrder,
    removeSelectedProductId,
    goBack,
    onClickContinue,
    purchasekey = undefined,
    setUserData,
    children,
    purchaseFlowType,
    showSelectProductError = $bindable(false),
    avaliableProducts = $bindable({}),
  }: Props = $props();

  const client = useQueryClient();

  // let avaliableProducts: AvaliableProducts = $state({});
  let canContinue = $state(purchasekey ? false : true);
  let checkPurchaseNotFound = $state(false);
  let checkPurchaseExpired = $state(false);

  const parseProducts = (data: TResponse) => {
    avaliableProducts = getAvaliableProducts(
      data,
      estimation,
      productOrder.includes("fixed"),
      productOrder.includes("variable"),
      productOrder.includes("hourly")
    );
    if (selectedProductId) {
      let isProduct = getProductWithId(selectedProductId, avaliableProducts);
      if (!isProduct) {
        removeSelectedProductId();
      } else {
        selectedProduct = isProduct;
      }
    }
  };

  type CheckPurchaseParams = {
    purchasekey?: string;
    offerId: number | undefined;
  };

  let checkPurchaseParams: CheckPurchaseParams = {
    purchasekey,
    offerId: undefined,
  };

  const checkPurchaseQuery = useCheckPurchase(checkPurchaseParams, {
    enabled: false,
    onSuccess: (data) => {
      if (data.result.errorCode === CheckPurchaseEnum.CheckPurchaseApproved) {
        const facilityInformation: FacilityInformation = {
          address: data.result.deliveryAddress,
          city: data.result.deliveryCity,
          zipcode: data.result.deliveryZipcode,
        };

        const person: Person = {
          firstname: data.result.firstName,
          lastname: data.result.lastName,
          mobile: data.result.phoneNumber,
          email: data.result.email,
          socialSecurity: data.result.socialSecurity,
          address: data.result.address,
          city: data.result.city,
          zipcode: data.result.zipcode,
        };

        if (setUserData) {
          setUserData(facilityInformation, person, true);
        }
        canContinue = true;
      } else if (data.result.errorCode === CheckPurchaseEnum.CheckPurchaseNotFound) {
        checkPurchaseNotFound = true;
      } else if (data.result.errorCode === CheckPurchaseEnum.CheckPurchaseExpired) {
        checkPurchaseExpired = true;
      }
    },
  });

  const params = $state({
    zipcode: zipcode ?? "",
    code: campaign,
  });

  $effect(() => {
    if (zipcode) {
      params.zipcode = zipcode;
    }
  });

  const queryResult = useGetProducts(params, {
    enabled: false,
    onSuccess: (data) => {
      checkPurchaseParams.offerId = data.result.offer.id;
      if (data.result.offer.requiresLimePurchase) {
        $checkPurchaseQuery.refetch();
      } else {
        canContinue = true;
      }
      parseProducts(data);
    },
  });

  if (client.getQueryData(["products", params])) {
    const data = client.getQueryData<TResponse>(["products", params]);
    if (data && data.result.offer.requiresLimePurchase) {
      $checkPurchaseQuery.refetch();
    }
    if (data) {
      parseProducts(data);
    }
  }

  const fetchProducts = () => $queryResult.refetch();

  onMount(() => {
    if (!client.getQueryData(["products", params])) fetchProducts();
  });

  $effect(() => {
    if (selectedProduct) {
      showSelectProductError = false;
    }
  });
</script>

{#if $queryResult.status == "error"}
  {#if $queryResult.error && ($queryResult.error as any).result && "zipcode" in ($queryResult.error as any).result}
    <ZipCodeError refetchPrices={fetchProducts} bind:zipcode />
  {:else}
    <Error
      onClick={() => {
        $queryResult.refetch();
      }}
      >Något gick fel vid hämtning av priser
    </Error>
  {/if}
{:else if $queryResult.status == "loading"}
  <Card class="h-110 flex justify-center items-center">
    <Loader />
  </Card>
{:else if $queryResult.status == "success"}
  {#if checkPurchaseNotFound}
    <ErrorMessage>
      <Heading>Något gick fel.</Heading>
      <Text variant="primary">Kontakta kundservice på 0910-77 25 50</Text>
    </ErrorMessage>
  {:else if checkPurchaseExpired}
    <ErrorMessage>
      <Heading>Din session har gått ut.</Heading>
      <Text variant="primary">
        Du har köpt en laddbox och bekräftelse kommer till din mejl. För att ta del av erbjudandet
        för elavtal ber vi dig ringa in till vår kundservice på 0910-77 25 50
      </Text>
    </ErrorMessage>
  {:else}
    <form onsubmit={preventDefault(onClickContinue)} class="a">
      <ProductSelector {productOrder} bind:selectedProduct {avaliableProducts}>
        {@render children?.()}
      </ProductSelector>
      {#if showSelectProductError}
        <ErrorBanner text="För att fortsätta måste du först välja ett elpris" class="mb-3" />
      {/if}
      <Button
        class="w-full mb-3 mt-2"
        type="submit"
        animate={false}
        disabled={purchaseFlowType == "v1" ? !selectedProduct || !canContinue : false}
        >Fortsätt</Button
      >
    </form>
  {/if}

  {#if !checkPurchaseExpired && !checkPurchaseNotFound}
    <span class="text-Neutral700 text-S">
      Baserat på en förbrukning på {estimation} kWh/år för postnummer {zipcode}.
      <button
        type="button"
        onclick={() => {
          goBack();
        }}
        class=" text-Violet700 hover:underline">Ändra uppgifter.</button
      >
      Din slutliga månadskostnad kommer att beräknas på din faktiska elanvändning. Elnätskostnad tillkommer.
      <br />
      Läs här om
      <a
        href="https://www.skekraft.se/kundservice/konsumentrattigheter/"
        target="_blank"
        class="text-Violet700 hover:underline">dina konsumenträttigheter</a
      >.
    </span>
  {/if}
{/if}
